import { Check, XCircle } from "lucide-react";
import { getBookingTimeline } from "../utils/bookingTimeline";

/**
 * Compact, at-a-glance payment/status timeline for the operator booking cards
 * (Bookings + Schedule tabs). A simple dot · line · dot strip that auto-updates
 * from the booking's live state. Surfaces "Owes $X" inline when a balance is due.
 */
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const dotClasses = (step) => {
  if (step.done) {
    // BookingFlows blue (#2563EB) for every completed step — never green.
    return "w-4 h-4 rounded-full bg-blue-600 flex items-center justify-center shrink-0";
  }
  if (step.warn) {
    return "w-4 h-4 rounded-full border border-amber-400 bg-amber-50 shrink-0";
  }
  return "w-4 h-4 rounded-full border border-gray-300 bg-white shrink-0";
};

export const BookingTimeline = ({ booking }) => {
  const code = booking?.confirmation_code || booking?.confirmationCode || "x";
  const { cancelled, steps } = getBookingTimeline(booking);

  if (cancelled) {
    return (
      <div
        data-testid={`booking-timeline-${code}`}
        className="flex items-center gap-1.5 pt-2 mt-1 text-xs text-red-500"
      >
        <XCircle className="w-3.5 h-3.5" />
        <span className="font-medium">Cancelled</span>
      </div>
    );
  }

  if (!steps || steps.length === 0) return null;

  return (
    <div
      data-testid={`booking-timeline-${code}`}
      className="flex items-start pt-2 mt-1 overflow-x-auto no-scrollbar"
    >
      {steps.map((step, i) => (
        <div key={step.key} className="flex items-start shrink-0">
          <div className="flex flex-col items-center min-w-[56px] px-0.5">
            <div className={dotClasses(step)} data-testid={`booking-timeline-step-${step.key}`}>
              {step.done && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
            </div>
            <span
              className={`mt-1 text-[10px] leading-tight text-center font-medium ${
                step.warn ? "text-amber-600" : step.done ? "text-gray-700" : "text-gray-400"
              }`}
            >
              {step.label}
            </span>
            {step.at && (
              <span className="text-[9px] leading-tight text-gray-400">{fmtDate(step.at)}</span>
            )}
          </div>
          {i < steps.length - 1 && (
            <div
              className={`h-0.5 w-4 sm:w-6 mt-2 shrink-0 ${
                step.done ? "bg-blue-600" : "bg-gray-200"
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export default BookingTimeline;
